import { useHomeStore } from '../../store/home-store';
import { EventCard } from '../event-card';
import { ShortMarketCard } from '../short-market-card';
import { groupMarketsByEvent, type EventMarketGroup } from './event-groups';
import { LightningEmpty } from './watch-list-empty';
import { MarketCardListSkeleton } from '@/client/components/page-skeletons/market-card-skeleton';
import { Button } from '@/client/components/ui/button';
import { BASE_TOKEN_CONFIG, sameAddress } from '@/client/confs/app';
import { useIsShortRoundMarket } from '@/client/pages/btc-5m/use-rounds-feed';
import { useAppSettingStore } from '@/client/store/app-store';
import { cn } from '@/client/utils';
import { api } from '@/client/utils/api';
import { useMarkets } from '@/libs/react-sdk/api/use-markets';
import { Market, MarketsParams, MarketsSortType, MarketStatus } from '@/libs/sdk/api';
import useInViewport from 'ahooks/lib/useInViewport';
import { useMemo, useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import useMedia from 'react-use/lib/useMedia';

// Moved to components/page-skeletons (see note there); re-exported to keep
// the module's public surface unchanged.
export { MarketCardListSkeleton };

const sameMarkets = (a: Market[], b: Market[]): boolean =>
  a.length === b.length && a.every((market, index) => market === b[index]);

/**
 * Reuse the previous group object (and its markets array) for any event whose
 * markets are referentially unchanged. Appending a page re-groups the whole
 * list, which would otherwise hand every EventCard a brand-new markets array
 * and defeat React.memo — this keeps unchanged groups referentially stable.
 */
// eslint-disable-next-line react-refresh/only-export-components -- pure helper kept beside its only caller (tested here)
export const reconcileEventGroups = (prev: EventMarketGroup[] | null, next: EventMarketGroup[]): EventMarketGroup[] => {
  if (!prev) return next;
  const prevById = new Map(prev.map(group => [group.eventId, group]));
  return next.map(group => {
    const previous = prevById.get(group.eventId);
    return previous && sameMarkets(previous.markets, group.markets) ? previous : group;
  });
};

export const TheMarketCardList = () => {
  const { t } = useTranslation(['home']);
  const isDesktop = useMedia('(min-width: 768px)');
  const ref = useRef<HTMLDivElement>(null);
  const pageSize = useMemo(() => (isDesktop ? 16 : 10), [isDesktop]);
  const category = useHomeStore(state => state.category);
  const tag = useHomeStore(state => state.tag);
  const sortDirection = useHomeStore(state => state.sortDirection);
  const sortType = useHomeStore(state => state.sortType);
  const selectedBaseToken = useAppSettingStore(state => state.baseToken);
  const setBaseToken = useAppSettingStore(state => state.setBaseToken);
  const searchParams = useMemo(() => {
    const params: MarketsParams = { page: 1, pageSize };
    // Pre-wire the server-side lens filter: current backend ignores unknown
    // params (harmless), the client-side filter below stays as the fallback
    // until the market-service filter ships
    params.baseTokenAddress = selectedBaseToken.address;
    if (tag) params.tag = tag;
    switch (category) {
      case 'trending':
        params.minVolumeTrending = true;
        break;
      case 'new':
        params.newMarket = true;
        break;
      case 'live':
        params.soonExpired = true;
        break;
      case 'ended':
        params.status = 5 as MarketStatus; // Special: expired markets (deadline < now), all statuses
        break;
      case 'watchlist':
        params.followed = true;
        break;
      default:
        if (category) params.categoryId = category;
    }
    switch (sortType) {
      case 'expire-time':
        params.sortType = MarketsSortType.expireTime;
        break;
      case 'volume':
        params.sortType = MarketsSortType.volume;
        break;
    }
    switch (sortDirection) {
      case 'asc':
        params.sortAsc = true;
        break;
      case 'desc':
        params.sortAsc = false;
        break;
    }
    return params;
  }, [category, pageSize, selectedBaseToken.address, sortDirection, sortType, tag]);
  const { data, isLoading, isFetching, hasNextPage, isFetchingNextPage, fetchNextPage } = useMarkets(api, searchParams);
  // Scope the feed to the currently selected base token (currency toggle): a market
  // carries its own baseTokenAddress (from get-markets), so DPN markets hide under the
  // DPS lens and vice versa. Markets without the field (single-base tenants like
  // DID/AIP whose backend does not send it) are always kept — no behavior change there.
  // Each 5-minute round is its own event (one per token): the series card stands for all of them, so
  // live lists never show them as cards. The ended view keeps settled rounds as history (founder 10/3).
  const isShortRound = useIsShortRoundMarket();
  const keepRounds = category === 'ended';
  const marketList = useMemo(
    () =>
      (data?.pages.flatMap(item => item.markets) ?? []).filter(
        market =>
          (!market.baseTokenAddress || sameAddress(market.baseTokenAddress, selectedBaseToken.address)) &&
          (keepRounds || !isShortRound(market))
      ),
    [data, selectedBaseToken.address, isShortRound, keepRounds]
  );
  // Keep unchanged event groups referentially stable across page appends so the
  // memoized cards below don't re-render when only new pages arrive.
  const prevGroupsRef = useRef<EventMarketGroup[] | null>(null);
  // The ref holds the previous memo result on purpose (see reconcileEventGroups).
  /* eslint-disable react-hooks/refs */
  const groupedMarkets = useMemo(() => {
    const next = groupMarketsByEvent(marketList);
    const stable = reconcileEventGroups(prevGroupsRef.current, next);
    prevGroupsRef.current = stable;
    return stable;
  }, [marketList]);
  /* eslint-enable react-hooks/refs */
  const isEmpty = !hasNextPage && marketList.length === 0;
  // Currency lens affordances only make sense on multi-base tenants (DPIN
  // DPS+DPN); single-base tenants keep the original header/empty state
  const isMultiBase = BASE_TOKEN_CONFIG.length > 1;
  const otherBaseToken = useMemo(
    () => BASE_TOKEN_CONFIG.find(item => !sameAddress(item.address, selectedBaseToken.address)),
    [selectedBaseToken.address]
  );

  // Infinite scroll sentinel
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [inViewport] = useInViewport(sentinelRef);

  useEffect(() => {
    if (inViewport && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [inViewport, hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Track filter changes for transition animation
  const [isTransitioning, setIsTransitioning] = useState(false);
  const prevFilterRef = useRef({ category, tag, sortType, sortDirection });

  useEffect(() => {
    const prev = prevFilterRef.current;
    const hasFilterChanged =
      prev.category !== category || prev.tag !== tag || prev.sortType !== sortType || prev.sortDirection !== sortDirection;

    if (hasFilterChanged) {
      setIsTransitioning(true);
      prevFilterRef.current = { category, tag, sortType, sortDirection };
      // Reset transition state after animation
      const timer = setTimeout(() => setIsTransitioning(false), 150);
      return () => clearTimeout(timer);
    }
  }, [category, tag, sortType, sortDirection]);

  // Show skeleton during initial load
  const showSkeleton = isLoading || (isFetching && marketList.length === 0);
  // Only show empty state when not loading and truly empty: the 5-minute card counts as a market.
  const [shortCardShown, setShortCardShown] = useState(false);
  const showEmpty = !isLoading && !isFetching && isEmpty && !shortCardShown;

  return (
    <section className="px-4 md:px-6 py-4">
      {/* Section header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <h2 className="text-base font-semibold">{t('all-markets', 'All Markets')}</h2>
          {/* Lens chip: declares that the list below is scoped to the selected currency */}
          {isMultiBase && (
            <span
              title={t(`translation:${selectedBaseToken.tooltipKey}`)}
              className="inline-flex items-center gap-1 h-5 px-1.5 rounded-full border border-foreground/8 bg-foreground/4 text-xs font-medium text-muted-foreground"
            >
              <selectedBaseToken.icon className="size-3.5" />
              {t(`translation:${selectedBaseToken.translateKey}`)}
            </span>
          )}
        </div>
        <span
          className={cn(
            'text-xs text-muted-foreground transition-opacity duration-200',
            (isTransitioning || showSkeleton) && 'opacity-50'
          )}
        >
          {marketList.length > 0 &&
            `${marketList.length}+ ${isMultiBase ? `${selectedBaseToken.symbol} ` : ''}${t('markets', 'markets')}`}
        </span>
      </div>

      <div
        ref={ref}
        className={cn(
          'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3',
          'transition-opacity duration-200 ease-out',
          isTransitioning && 'opacity-60'
        )}
      >
        {/* The BTC 5-minute series leads the default view (it hides itself when nothing trades) */}
        {category === 'trending' && !tag && <ShortMarketCard onShownChange={setShortCardShown} />}

        {/* Show skeleton during initial load or refetch with no data */}
        {showSkeleton && <MarketCardListSkeleton len={pageSize} />}

        {/* Show market cards when we have data */}
        {!showSkeleton &&
          groupedMarkets.map((group, index) => (
            <EventCard
              key={`event-${group.eventId}-${index}`}
              eventId={group.eventId}
              markets={group.markets}
              index={index}
            />
          ))}

        {/* Show loading skeleton when fetching next page */}
        {isFetchingNextPage && <MarketCardListSkeleton len={pageSize} />}
      </div>

      {/* Empty state - only show when not loading and truly empty */}
      {showEmpty &&
        (category == 'watchlist' || category == 'live' ? (
          <LightningEmpty
            title={category == 'watchlist' ? t('watchlist') : t('nothing-right-now')}
            desc={
              category == 'watchlist'
                ? t('follow-predictions-you-care-about')
                : t('markets-ending-within-48-hours-will-appear-here')
            }
          />
        ) : isMultiBase && otherBaseToken ? (
          // Currency-aware empty state with a one-click escape to the other lens
          <LightningEmpty
            title={t('no-markets-for-currency', { symbol: selectedBaseToken.symbol })}
            showButton={false}
            action={
              <Button
                variant="outline"
                className="h-[28px] px-4 py-2 text-xs font-medium rounded-full"
                onClick={() => setBaseToken(otherBaseToken)}
              >
                {t('browse-other-currency', { symbol: otherBaseToken.symbol })}
              </Button>
            }
          />
        ) : (
          <LightningEmpty title={t('translation:nothing-yet')} showButton={false} />
        ))}

      {hasNextPage && <div ref={sentinelRef} className="h-1" />}
      {isFetchingNextPage && <MarketCardListSkeleton len={pageSize} />}
    </section>
  );
};
